/**
 * MyRequirements.jsx — Buyer lists their posted requirements and opens bids.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';

function formatTimeline(req) {
  const weeks = Number(req.timeline_weeks);
  if (Number.isFinite(weeks) && weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''}`;
  return req.timeline_end || '—';
}

export default function MyRequirements() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    apiClient
      .get('/api/v1/requirements/mine/')
      .then(({ data }) => setItems(Array.isArray(data) ? data : data.results ?? []))
      .catch(() => setError('Could not load your requirements. Please try again.'))
      .finally(() => setLoading(false));
  }, [location.key]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress aria-label="Loading requirements" />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{
          mb: 3,
          textTransform: 'none',
          borderColor: 'rgba(255,255,255,0.12)',
          color: '#cbd5e1',
          '&:hover': {
            borderColor: '#818cf8',
            bgcolor: 'rgba(129, 140, 248, 0.04)',
          }
        }}
      >
        Back to Home
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">My requirements</Typography>
        <Button variant="contained" onClick={() => navigate('/buyer/requirements/new')}>
          Post new requirement
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {items.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.01)', borderStyle: 'dashed', borderWidth: '1.5px', borderColor: 'rgba(255, 255, 255, 0.12)', borderRadius: '16px', mt: 3 }}>
          <Box sx={{ color: 'primary.light', mb: 2.5, display: 'flex', justifyContent: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>No Requirements Posted Yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: 'auto', mb: 3.5, lineHeight: 1.65 }}>
            Post your cybersecurity project needs and compliance criteria anonymously. Our secure matching engine will pair you with certified vendors in seconds.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate('/buyer/requirements/new')} sx={{ px: 4, py: 1.2 }}>
            Post a Requirement
          </Button>
        </Paper>
      ) : (
        items.map((req) => (
          <Paper key={req.id} sx={{ p: 2, mb: 2 }} elevation={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant="h6">{req.category}</Typography>
              <Chip label={req.status} size="small" />
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              ID #{req.id} · timeline {formatTimeline(req)}
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 1.5 }}
              onClick={() => navigate(`/buyer/requirements/${req.id}/bids`)}
              aria-label={`View bids for requirement ${req.id}`}
            >
              View bids
            </Button>
          </Paper>
        ))
      )}
    </Box>
  );
}
